import { useEffect, useLayoutEffect, useReducer, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Page loader store — which pages are still on their FIRST load.
 *
 * Pages register through `usePageLoader(loading, {brand, label})`; the single
 * `PageLoaderHost` (mounted once in App) covers the viewport while any entry is
 * active. A module-level store (not context) so a page can register from any
 * branch of its render tree — early returns included — and the overlay
 * survives the page switching from its loading shell to the real layout.
 */

const entries = new Map(); // id → { brand, label }
// Pages with the hook MOUNTED (loading or not) — the host only covers a route
// change that starts on one of them.
const mountedPages = new Set();

export function hasPageLoaderPages() {
  return mountedPages.size > 0;
}
const listeners = new Set();
let snapshot = { active: false, brand: 'CLS', label: undefined };

function emit() {
  const last = Array.from(entries.values()).pop();
  snapshot = last
    ? { active: true, brand: last.brand, label: last.label }
    : { active: false, brand: snapshot.brand, label: snapshot.label };
  listeners.forEach((fn) => fn());
}

export function setPageLoading(id, entry) {
  if (entry) entries.set(id, entry);
  else if (!entries.delete(id)) return;
  emit();
}

export function subscribePageLoader(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getPageLoaderSnapshot() {
  return snapshot;
}

/** Test helper — drop every registration. */
export function resetPageLoader() {
  entries.clear();
  mountedPages.clear();
  emit();
}

let nextId = 0;

/**
 * Cover the page with the brand loader until its data for the CURRENT URL
 * path is in.
 *
 * LATCHED per path: once `loading` has been false, later refetches on the
 * same path (a class switch only changes `?class=`) keep the page visible and
 * fall back to the page's own inline `Preloader`. A new path on the SAME
 * mounted page — Vogntrøjer → Studieturstrøjer group, school-root hop, product
 * → product — re-arms it (`key` overrides the path when a page needs another
 * notion of "new content").
 *
 * The render right after a path change still carries the OLD data and
 * `loading === false` (the data hook only flips `loading` in its effect), so
 * the new path counts as PENDING: covered at once — before the stale grid can
 * paint — until `loading` turns true and back, or one frame passes without a
 * refetch starting (then it is released, so a page that does not refetch is
 * never stuck behind the loader).
 *
 * Registered in a LAYOUT effect so the overlay is up before the browser paints
 * the header/footer of a page with no data.
 */
export function usePageLoader(loading, { brand = 'CLS', label, key } = {}) {
  const { pathname } = useLocation();
  const pageKey = key ?? pathname;
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = `page-loader-${++nextId}`;
  const keyRef = useRef(pageKey);
  const doneRef = useRef(false);
  const pendingRef = useRef(false);
  const [, release] = useReducer((n) => n + 1, 0);

  if (keyRef.current !== pageKey) {
    keyRef.current = pageKey;
    doneRef.current = false;
    pendingRef.current = true;
  }
  if (loading) pendingRef.current = false;
  else if (!pendingRef.current) doneRef.current = true;
  const active = (Boolean(loading) || pendingRef.current) && !doneRef.current;

  useLayoutEffect(() => {
    setPageLoading(idRef.current, active ? { brand, label } : null);
  }, [active, brand, label]);

  // Pending path with no refetch started by the time effects ran → release.
  useEffect(() => {
    if (!pendingRef.current || loading) return undefined;
    const frame = requestAnimationFrame(() => {
      if (!pendingRef.current) return;
      pendingRef.current = false;
      release();
    });
    return () => cancelAnimationFrame(frame);
  }, [loading, pageKey]);

  useLayoutEffect(() => {
    const id = idRef.current;
    mountedPages.add(id);
    return () => {
      mountedPages.delete(id);
      setPageLoading(id, null);
    };
  }, []);
}
